import { useCallback, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';

import {
    RECORDING_MAIN_METADATA_LANGUAGE,
    getRecordingsUrl,
} from 'config/api.ts';

import isRecordingsResponse from 'store/details/utils/isRecordingsResponse.ts';
import {
    RecordingsSeasonsResponse,
    ResponseError,
} from 'store/railsBuilder/reducer.types.ts';
import { FetchRecordingsSortType } from 'store/recordings/actions.types.ts';
import {
    RecordingItem,
    RecordingResponse,
} from 'store/recordings/reducer.types.ts';

import NagraInstance from 'utils/axios/nagra/DefaultInstance.ts';

import { noSeasonName } from '../constants.ts';
import { EpisodesState } from '../types.ts';
import requestRecordingEpisodesBySeason from '../utils/requestRecordingEpisodesBySeason.ts';

type Params = {
    seriesId: string;
    initialSeason: number | typeof noSeasonName;
    selectedSeasonNumber: number | typeof noSeasonName;
    recordingSeasons: RecordingsSeasonsResponse['seasons'];
};

const EPISODES_LIMIT = 10;

function useRecordingEpisodes({
    seriesId,
    initialSeason,
    selectedSeasonNumber,
    recordingSeasons,
}: Params) {
    const [recordingsState, setRecordingsState] = useState<
        EpisodesState<RecordingItem>
    >({
        [initialSeason]: { episodes: [], page: 1, maxPages: undefined },
    });

    const episodes = useMemo(
        () => recordingsState[selectedSeasonNumber]?.episodes || [],
        [recordingsState, selectedSeasonNumber],
    );

    const handleLoadNPVRProgramSeasonEpisodes = useCallback(
        async (
            _seasonNumber: typeof initialSeason,
            _seasonsList: RecordingsSeasonsResponse['seasons'] | undefined,
            _page: number,
        ) => {
            const season = _seasonsList?.find(
                ({ seasonNumber }) =>
                    // eslint-disable-next-line eqeqeq
                    seasonNumber == _seasonNumber,
            );

            if (season) {
                let recordingsBySeasonResponse: AxiosResponse<
                    RecordingResponse | ResponseError
                >;

                if (season.seasonId === noSeasonName) {
                    recordingsBySeasonResponse =
                        await NagraInstance.get<RecordingResponse>(
                            getRecordingsUrl({
                                filter: {
                                    [`metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.SeasonNumber`]: 0,
                                    seriesId,
                                },
                                sort: FetchRecordingsSortType.CHRONOLOGICAL_DESC,
                                page: _page,
                                limit: EPISODES_LIMIT,
                            }),
                        );
                } else {
                    recordingsBySeasonResponse =
                        await requestRecordingEpisodesBySeason(
                            season.seasonId,
                            {
                                page: _page,
                                limit: EPISODES_LIMIT,
                            },
                        );
                }

                if (isRecordingsResponse(recordingsBySeasonResponse)) {
                    setRecordingsState((prevState) => ({
                        ...prevState,
                        [season.seasonNumber]: {
                            ...prevState[season.seasonNumber],
                            episodes:
                                _page === 1
                                    ? recordingsBySeasonResponse.data
                                          .recordingSet
                                    : [
                                          ...(prevState[season.seasonNumber]
                                              ?.episodes || []),
                                          ...recordingsBySeasonResponse.data
                                              .recordingSet,
                                      ],
                            page: _page,
                            maxPages:
                                _page === 1
                                    ? Math.ceil(
                                          recordingsBySeasonResponse.data
                                              .totalRecords / EPISODES_LIMIT,
                                      )
                                    : prevState[season.seasonNumber]?.maxPages,
                        },
                    }));
                }
            }
        },
        [],
    );

    const handleLoadNextEpisodes = useCallback(async () => {
        const { page, maxPages } = recordingsState[selectedSeasonNumber] || {};
        const nextPage = page + 1;

        if (maxPages != null && nextPage <= maxPages) {
            await handleLoadNPVRProgramSeasonEpisodes(
                selectedSeasonNumber,
                recordingSeasons,
                nextPage,
            );
        }
    }, [
        handleLoadNPVRProgramSeasonEpisodes,
        recordingSeasons,
        recordingsState,
        selectedSeasonNumber,
    ]);

    return {
        episodes,
        onLoadEpisodes: handleLoadNPVRProgramSeasonEpisodes,
        onLoadNextEpisodes: handleLoadNextEpisodes,
    };
}

export default useRecordingEpisodes;
